import React from 'react';
import { Link } from 'react-router-dom';
import BlurFade from '../primitives/BlurFade';
import Pill from '../ui/Pill';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import { ArrowRight } from 'lucide-react';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * GridBlock — universal card grid (Products, future Werkwijze-tiles, etc.).
 * variant='3-up' renders 3 columns ≥ lg, 2 ≥ md, 1 on mobile.
 *
 * Each item: { key, name, scene, desc, image, href?, ctaLabel? }
 *   When `href` is set, the per-card CTA uses an internal Link to that path
 *   (e.g. category landing pages /ramen). Otherwise it falls back to the
 *   block-level `cta` (anchor link).
 */
export default function GridBlock({
  id, testId, tone = 'cream', rhythm = 'relaxed',
  eyebrow, title, items = [], cta,
  variant = '3-up',
}) {
  const { lang, overlay } = useLang();
  const cols = variant === '3-up' ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2';
  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container>
        <div className="max-w-3xl mb-14 lg:mb-20">
          {eyebrow && <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>}
          {title && <BlurFade delay={0.05}><Headline size="xl">{title}</Headline></BlurFade>}
        </div>

        <div className={`grid grid-cols-1 ${cols} gap-6 lg:gap-8`}>
          {items.map((p, i) => (
            <BlurFade key={p.key} delay={0.06 * i} offset={18}>
              <article
                id={p.key}
                data-testid={`product-card-${p.key}`}
                className="group relative h-full flex flex-col rounded-[20px] overflow-hidden bg-white border border-[#E5E0D2] transition-all duration-500 hover:border-[#F26B1A]/40 hover:shadow-[0_30px_60px_-30px_rgba(90,128,86,0.35)] hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EEDF]">
                  <img
                    src={p.image}
                    alt={mediaAlt(overlay, p.image, lang, p.name || '')}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-[1.4s] ease-out group-hover:scale-[1.06] group-hover:opacity-0"
                  />
                  {p.imageHover && (
                    <img
                      src={p.imageHover}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                  {p.badge && (
                    <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.15em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
                      {p.badge}
                    </span>
                  )}
                  {p.price && (
                    <span
                      className={`absolute bottom-4 right-4 inline-flex items-baseline gap-2 px-3 py-1.5 rounded-full backdrop-blur-md border text-[12.5px] font-semibold tracking-wide shadow-[0_4px_14px_-4px_rgba(31,29,26,0.18)] ${
                        typeof p.price === 'object' && p.price.isSale
                          ? 'bg-[#F26B1A] text-white border-white/10'
                          : 'bg-white/95 text-[#1F1D1A] border-black/5'
                      }`}
                    >
                      {typeof p.price === 'string' ? (
                        p.price
                      ) : (
                        <>
                          {p.price.isSale && p.price.saleLabel && (
                            <span className="text-[10px] uppercase tracking-[0.15em] opacity-90 mr-1">{p.price.saleLabel}</span>
                          )}
                          {p.price.display}
                          {p.price.isSale && (
                            <span className="line-through opacity-70 text-[11px] font-normal">{p.price.base}</span>
                          )}
                        </>
                      )}
                    </span>
                  )}
                </div>

                <div className="flex flex-col flex-1 p-6 lg:p-7">
                  <h3 className="font-display text-[#1F1D1A] text-[24px] lg:text-[28px] leading-tight">
                    {p.name}
                  </h3>
                  {p.scene && (
                    <p className="mt-2 font-display text-[#F26B1A] text-[16.5px] lg:text-[18px] leading-snug font-light">
                      {p.scene}
                    </p>
                  )}
                  {p.desc && (
                    <Body className="mt-4 text-[14px] leading-relaxed">{p.desc}</Body>
                  )}
                  {cta && (
                    p.href ? (
                      <Link
                        to={p.href}
                        data-testid={`product-cta-${p.key}`}
                        data-lead-source={`product-${p.key}`}
                        className="mt-6 inline-flex items-center gap-1.5 text-[#1F1D1A] text-[13.5px] font-medium link-underline w-fit group-hover:text-[#F26B1A] transition-colors"
                      >
                        {p.ctaLabel || cta.label}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    ) : (
                      <a
                        href={cta.href}
                        data-testid={`product-cta-${p.key}`}
                        data-lead-source={`product-${p.key}`}
                        className="mt-6 inline-flex items-center gap-1.5 text-[#1F1D1A] text-[13.5px] font-medium link-underline w-fit group-hover:text-[#F26B1A] transition-colors"
                      >
                        {cta.label}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    )
                  )}
                </div>
              </article>
            </BlurFade>
          ))}
        </div>
      </Container>
    </Section>
  );
}
